import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

export type Effort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export interface AssistantModel {
  id: string;
  label: string;
  costTier: 1 | 2 | 3 | 4;
  efforts: Effort[];
}

export interface AssistantOptions {
  available: boolean;
  /** Why not, when not: assistant.disabled, assistant.not_permitted, assistant.not_configured. */
  reason: string | null;
  connected: boolean;
  keyHint: string | null;
  /** Has already asked from the Claude app through the Diwan connector. */
  claudeLinked: boolean;
  canManage: boolean;
  models: AssistantModel[];
  defaultModel: string;
  defaultEffort: Effort;
  connectorUrl: string | null;
}

export interface ConversationSummary {
  id: string;
  title: string;
  model: string;
  updatedAt: string;
}

export interface ToolTable {
  columns: string[];
  rows: Record<string, unknown>[];
  truncated: boolean;
}

/** One lookup the assistant made while answering. */
export interface ToolStep {
  id: string;
  name: string;
  input: unknown;
  done: boolean;
  isError?: boolean;
  rowCount?: number | null;
  table?: ToolTable;
}

export interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;
  thinking?: string;
  steps: ToolStep[];
  error?: { code: string; message: string };
  streaming?: boolean;
}

export const OPTIONS_KEY = ['/assistant/options'];

export function useAssistantOptions(enabled = true) {
  return useQuery({
    queryKey: OPTIONS_KEY,
    queryFn: () => api.get<AssistantOptions>('/assistant/options'),
    enabled,
    // A workspace without the feature answers 503 for everyone; do not retry
    // it three times on every page.
    retry: false,
  });
}

export function useConversations(enabled: boolean) {
  return useQuery({
    queryKey: ['/assistant/conversations'],
    queryFn: () => api.get<ConversationSummary[]>('/assistant/conversations'),
    enabled,
  });
}
